/**
 * Gate a route on platform-staff authority (ADR-0015 §2.1).
 *
 * A **redirect**, not a refusal message: unlike `principal_unbound`, "you are a Merchant
 * user" is not a mistake to explain — the console is simply not their screen, and the
 * Orders they came for are one route away. The API refuses independently, so this is a
 * navigation courtesy and never the control itself. *
 * **It says why now (ADR-0085), for an Ops user and not for a Merchant one.** The redirect
 * above was the whole answer, so an operator following a colleague's link arrived at the
 * landing page with the URL gone from the address bar and nothing to go on. A Merchant user
 * still goes home, because that reasoning holds for them: this is not their screen and their
 * own is one route away. An Ops user who lacks one responsibility is told which.
 */

import type { ReactNode } from 'react';
import { Navigate } from 'react-router-dom';
import { useSession } from './session.js';
import { AuthorityRefused } from './authority-refused.js';

export function RequirePlatformStaff({ children }: { readonly children: ReactNode }): React.JSX.Element {
  const { state } = useSession();

  if (state.status !== 'authenticated') return <>{children}</>;
  if (state.session.role !== 'opsUser') return <Navigate to="/" replace />;
  if (!state.session.platformStaff) return <AuthorityRefused needs={{ kind: 'staff' }} />;

  return <>{children}</>;
}
